<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">발송 경로 관리</h1>
            <a-button :loading="listLoading || historyLoading" @click="reloadAll">다시 읽기</a-button>
          </div>
          <span class="pageDesc">
            알림톡·문자·브랜드메시지를 v1(기존 inapi)과 v2(발송 서버) 중 어느 길로 보낼지 정합니다.
            회사 개별 설정이 있으면 전체 기본값보다 그 설정이 이깁니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="card-body px-0 pt-3 pb-2">
      <!-- 안내 (REQ-402) -->
      <a-alert
        v-if="loaded && !dispatcherConfigured"
        type="error"
        show-icon
        class="notice"
        message="발송 서버 설정이 없어 v2 로 둬도 v1 으로 나갑니다"
        description="crm-api 에 발송 서버 주소·키가 설정되지 않았습니다. 설정이 들어가기 전까지는 v2 로 바꿔도 실제 발송은 v1(기존 inapi)로 나갑니다."
      />
      <a-alert type="info" show-icon class="notice" message="바꾼 값은 최대 30초 뒤 새 발송부터 적용됩니다.">
        <template #description>
          <ul class="notice-list">
            <li>이미 보낸 건과 경로가 고정된 캠페인·예약 건은 처음 길로 끝납니다.</li>
            <li>브랜드메시지는 재판매사 코드(svatbo_no)를 받아 시험하기 전까지 v2 로 바꿀 수 없습니다.</li>
            <li>v2(발송 서버)는 알림톡·브랜드메시지 템플릿 관리(목록·등록·수정·삭제·이미지)도 발송 서버를 거칩니다. 문자는 해당 없음.</li>
          </ul>
        </template>
      </a-alert>

      <!-- inapi·mq 서비스 전체 v1/v2 (2026-10-27) -->
      <ServiceRouteSwitches />

      <!-- 전체 기본값 -->
      <div class="card content-wrap-card">
        <h2 class="section-title">전체 기본값 (고객사 전체 일괄 적용)</h2>
        <p class="section-hint">
          개별 설정이 없는 회사에 적용됩니다. 바꿔도 회사 개별 설정은 그대로이며, 모든 회사가 따르게 하려면 아래
          '개별 설정 모두 지우기'를 쓰세요.
        </p>
        <div v-for="c in CHANNELS" :key="c.key" class="route-row">
          <span class="route-channel">{{ c.label }}</span>
          <a-radio-group
            :value="defaultRouteOf(defaults, c.key)"
            :options="optionsFor(c.key, DEFAULT_ROUTE_OPTIONS)"
            option-type="button"
            button-style="solid"
            :disabled="!loaded || saving"
            @change="(e) => confirmSetDefault(c.key, e.target.value)"
          />
          <span v-if="defaultRow(c.key)" class="route-meta">
            마지막 변경 {{ formatTime(defaultRow(c.key).updatedAt) }} · {{ actorLabel(defaultRow(c.key).updatedBy, accounts) }}
          </span>
        </div>
      </div>

      <!-- 회사별 설정 -->
      <div class="card content-wrap-card">
        <h2 class="section-title">회사별 설정</h2>
        <a-input-search
          v-model:value="companyKeyword"
          placeholder="병원명을 입력하고 검색하세요"
          enter-button="검색"
          :loading="companyLoading"
          class="company-search"
          @search="searchCompanies"
        />
        <!-- 검색 결과에서 병원을 클릭해 선택한다 -->
        <div v-if="companyResults.length" class="company-results">
          <div
            v-for="co in companyResults"
            :key="co._id"
            class="company-result-item"
            :class="{ selected: selectedCompany && selectedCompany._id === co._id }"
            @click="selectCompany(co)"
          >
            {{ co.name }}
            <div class="company-detail">{{ companyDetail(co) }}</div>
          </div>
        </div>
        <template v-if="selectedCompany">
          <div class="selected-company">
            선택된 병원: <strong>{{ selectedCompany.name }}</strong>
            <div class="company-detail">{{ companyDetail(selectedCompany) }}</div>
          </div>
          <div v-for="c in CHANNELS" :key="c.key" class="route-row">
            <span class="route-channel">{{ c.label }}</span>
            <a-radio-group
              :value="selectedStates[c.key]"
              :options="optionsFor(c.key, COMPANY_STATE_OPTIONS)"
              option-type="button"
              button-style="solid"
              :disabled="!loaded || saving"
              @change="(e) => changeCompany(c.key, e.target.value)"
            />
            <span v-if="selectedStates[c.key] === 'default'" class="route-meta">
              지금 적용: {{ routeLabel(defaultRouteOf(defaults, c.key)) }}
            </span>
          </div>
        </template>
        <p class="section-hint">'기본값 따름'을 고르면 그 회사의 개별 설정을 지워 전체 기본값을 따르게 합니다.</p>
      </div>

      <!-- 개별 설정 목록 -->
      <div class="card content-wrap-card">
        <div class="section-head">
          <h2 class="section-title">개별 설정 목록</h2>
          <a-space wrap>
            <a-button
              v-for="c in CHANNELS"
              :key="c.key"
              danger
              :disabled="!loaded || saving || !overrideCount(overrides, c.key)"
              @click="confirmClearAll(c.key)"
            >
              {{ c.label }} 개별 설정 모두 지우기 ({{ overrideCount(overrides, c.key) }})
            </a-button>
          </a-space>
        </div>
        <a-table
          :columns="overrideColumns"
          :data-source="overrides"
          :row-key="(r) => `${r.channel}:${r.companyId}`"
          :loading="listLoading"
          size="middle"
          :scroll="{ x: 900 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'company'">{{ companyLabel(record, companies) }}</template>
            <template v-else-if="column.key === 'updatedBy'">{{ actorLabel(record.updatedBy, accounts) }}</template>
            <template v-else-if="column.key === 'channel'">{{ channelLabel(record.channel) }}</template>
            <template v-else-if="column.key === 'route'">{{ routeLabel(record.route) }}</template>
            <template v-else-if="column.key === 'updatedAt'">{{ formatTime(record.updatedAt) }}</template>
            <template v-else-if="column.key === 'remove'">
              <a-popconfirm
                title="이 회사의 개별 설정을 지웁니다. 이 회사는 전체 기본값을 따릅니다."
                ok-text="지우기"
                cancel-text="취소"
                @confirm="clearCompany(record.companyId, record.channel)"
              >
                <a-button size="small" danger :disabled="saving">지우기</a-button>
              </a-popconfirm>
            </template>
          </template>
        </a-table>
      </div>

      <!-- 변경 이력 (REQ-404) -->
      <div class="card content-wrap-card">
        <h2 class="section-title">변경 이력</h2>
        <a-space wrap class="history-filter">
          <a-select v-model:value="historyChannel" :options="historyChannelOptions" style="width: 160px" @change="loadHistory(1)" />
          <a-checkbox v-model:checked="historyOnlySelected" :disabled="!selectedCompany" @change="loadHistory(1)">
            선택한 병원만 ({{ selectedCompany ? selectedCompany.name : "위에서 병원을 먼저 고르세요" }})
          </a-checkbox>
        </a-space>
        <a-table
          :columns="historyColumns"
          :data-source="historyRows"
          :loading="historyLoading"
          :pagination="historyPagination"
          row-key="key"
          size="middle"
          :scroll="{ x: 1100 }"
          @change="(p) => loadHistory(p.current)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { message, Modal } from "ant-design-vue";
import dayjs from "dayjs";
import apiInstance from "@/axios";
import { messageRouteApi } from "./messageRouteApi";
import ServiceRouteSwitches from "./components/ServiceRouteSwitches.vue";
import {
  CHANNELS,
  DEFAULT_ROUTE_OPTIONS,
  COMPANY_STATE_OPTIONS,
  ROUTE_OF_STATE,
  optionsFor,
  channelLabel,
  routeLabel,
  companyLabel,
  companyDetail,
  actorLabel,
  defaultRouteOf,
  companyStates,
  overrideCount,
  defaultConfirmText,
  clearAllConfirmText,
  historyRow,
} from "./messageRouteState";

const HISTORY_PAGE_SIZE = 20;
const FALLBACK_NOTICE = "저장했습니다. 바꾼 값은 최대 30초 뒤 새 발송부터 적용됩니다.";

const formatTime = (t) => (t ? dayjs(t).format("YYYY-MM-DD HH:mm:ss") : "-");

// crm(Nest) 오류 본문의 message 는 문자열 또는 문자열 배열이다.
const errorText = (error, fallback) => {
  const m = error && error.response && error.response.data && error.response.data.message;
  return m ? `${fallback} (${Array.isArray(m) ? m.join(", ") : m})` : fallback;
};

const overrideColumns = [
  { title: "회사", key: "company", width: 200 },
  { title: "채널", key: "channel", width: 110 },
  { title: "값", key: "route", width: 140 },
  { title: "바꾼 사람", dataIndex: "updatedBy", key: "updatedBy", width: 180 },
  { title: "바꾼 시각", key: "updatedAt", width: 170 },
  { title: "메모", dataIndex: "note", key: "note" },
  { title: "", key: "remove", width: 90, fixed: "right" },
];

const historyColumns = [
  { title: "시각", dataIndex: "at", key: "at", width: 170 },
  { title: "채널", dataIndex: "channel", key: "channel", width: 110 },
  { title: "회사", dataIndex: "company", key: "company", width: 200 },
  { title: "동작", dataIndex: "action", key: "action", width: 130 },
  { title: "이전 값", dataIndex: "before", key: "before", width: 140 },
  { title: "새 값", dataIndex: "after", key: "after", width: 140 },
  { title: "바꾼 사람", dataIndex: "by", key: "by", width: 180 },
  { title: "메모", dataIndex: "note", key: "note" },
  { title: "지운 수", dataIndex: "deleted", key: "deleted", width: 100 },
];

export default {
  name: "MessageRoutes",
  components: { ServiceRouteSwitches },
  setup() {
    // ---- 경로 목록 (서버 값이 진실: 쓰기 뒤에는 늘 다시 읽고, 화면 상태를 미리 바꾸지 않는다) ----
    const loaded = ref(false); // 목록을 읽기 전·읽기 실패 뒤에는 바꾸기를 막는다
    const listLoading = ref(false);
    const saving = ref(false);
    const defaults = ref([]);
    const overrides = ref([]);
    const dispatcherConfigured = ref(true);

    const loadList = async () => {
      listLoading.value = true;
      try {
        const { data } = await messageRouteApi.list();
        defaults.value = data.defaults || [];
        overrides.value = data.overrides || [];
        dispatcherConfigured.value = data.dispatcherConfigured === true;
        loaded.value = true;
        resolveNames();
      } catch (error) {
        loaded.value = false;
        console.error("발송 경로 목록 조회 실패:", error);
        message.error(errorText(error, "발송 경로 목록을 읽지 못했습니다. '다시 읽기'를 눌러 주세요."));
      } finally {
        listLoading.value = false;
      }
    };

    const defaultRow = (channel) => defaults.value.find((d) => d.channel === channel && d.updatedAt);

    // ---- 회사 검색 (AfterdocMigration.vue 와 같은 inapi 서버측 검색) ----
    const companyKeyword = ref("");
    const companyResults = ref([]);
    const companyLoading = ref(false);
    const selectedCompany = ref(null);

    const searchCompanies = async () => {
      const keyword = companyKeyword.value.trim();
      if (!keyword) {
        companyResults.value = [];
        return;
      }
      companyLoading.value = true;
      try {
        const response = await apiInstance.post("/companies/list/v2", {
          page: 1,
          limit: 50,
          type: "HOSPITAL",
          search: "name",
          condition: keyword,
        });
        companyResults.value = (response.data && response.data.docs) || [];
        if (!companyResults.value.length) {
          message.info("검색 결과가 없습니다. 병원명을 확인해주세요.");
        }
      } catch (error) {
        console.error("병원 검색 실패:", error);
        message.error("병원 검색에 실패했습니다.");
      } finally {
        companyLoading.value = false;
      }
    };

    const selectCompany = (company) => {
      selectedCompany.value = company;
      if (historyOnlySelected.value) loadHistory(1);
    };

    const selectedStates = computed(() =>
      selectedCompany.value ? companyStates(overrides.value, selectedCompany.value._id) : {}
    );

    // ---- 변경 이력 ----
    const historyChannel = ref("");
    const historyChannelOptions = [{ value: "", label: "모든 채널" }, ...CHANNELS.map((c) => ({ value: c.key, label: c.label }))];
    const historyOnlySelected = ref(false);
    const historyItems = ref([]);
    const historyLoading = ref(false);
    const historyPagination = ref({ current: 1, pageSize: HISTORY_PAGE_SIZE, total: 0, showSizeChanger: false });

    const loadHistory = async (page = historyPagination.value.current) => {
      historyLoading.value = true;
      try {
        const { data } = await messageRouteApi.history({
          channel: historyChannel.value || undefined,
          companyId: historyOnlySelected.value && selectedCompany.value ? selectedCompany.value._id : undefined,
          page,
          limit: HISTORY_PAGE_SIZE,
        });
        historyItems.value = data.items || [];
        historyPagination.value = { ...historyPagination.value, current: data.page || page, total: data.total || 0 };
        resolveNames();
      } catch (error) {
        console.error("변경 이력 조회 실패:", error);
        message.error(errorText(error, "변경 이력을 읽지 못했습니다."));
      } finally {
        historyLoading.value = false;
      }
    };

    // ---- 표시용 이름: crm 은 바꾼 사람·회사를 id 로만 남긴다(이력에 사람 이름을 담지 않음). 화면이 보일 때만 찾는다 ----
    const accounts = ref({}); // 계정 id → { name, username } | null(못 찾음)
    const companies = ref({}); // 회사 id → 이름 | null(못 찾음)
    const asked = new Set(); // 이미 물어본 id(동시에 읽어도 한 번만 묻는다)

    const lookup = async (kind, ids, fetchOne) => {
      const fresh = [...new Set(ids)].filter((id) => id && !asked.has(`${kind}:${id}`));
      fresh.forEach((id) => asked.add(`${kind}:${id}`));
      const found = await Promise.all(fresh.map((id) => fetchOne(id).then((v) => [id, v], () => [id, null])));
      return Object.fromEntries(found);
    };

    const resolveNames = async () => {
      const rows = [...overrides.value, ...historyItems.value];
      const [acc, co] = await Promise.all([
        lookup(
          "acc",
          [...defaults.value.map((d) => d.updatedBy), ...overrides.value.map((o) => o.updatedBy), ...historyItems.value.map((h) => h.by)],
          (id) => apiInstance.get(`/accounts/certilife/${id}`).then(({ data }) => ({ name: data.name, username: data.username }))
        ),
        lookup(
          "co",
          rows.filter((r) => r.companyId && !r.companyName).map((r) => r.companyId),
          (id) => apiInstance.get(`/companies/${id}`).then(({ data }) => data.name || null)
        ),
      ]);
      accounts.value = { ...accounts.value, ...acc };
      companies.value = { ...companies.value, ...co };
    };

    const historyRows = computed(() =>
      historyItems.value.map((item, i) => ({
        ...historyRow(item, { accounts: accounts.value, companies: companies.value }),
        key: `${historyPagination.value.current}-${i}`,
        at: formatTime(item.at),
        note: item.note || "",
      }))
    );

    const reloadAll = () => Promise.all([loadList(), loadHistory()]);

    // ---- 쓰기: 성공·실패 모두 끝나면 목록과 변경 이력을 함께 다시 읽는다 (REQ-401·404) ----
    const write = async (request) => {
      saving.value = true;
      try {
        const { data } = await request();
        message.success((data && data.notice) || FALLBACK_NOTICE);
      } catch (error) {
        console.error("발송 경로 저장 실패:", error);
        message.error(errorText(error, "저장하지 못했습니다. 지금 값을 다시 읽었으니 확인한 뒤 다시 시도해 주세요."));
      } finally {
        saving.value = false;
        await reloadAll();
      }
    };

    // 고객사 전체 일괄 적용 — 확인창을 거친다. 취소하면 아무것도 바뀌지 않는다.
    const confirmSetDefault = (channel, route) => {
      Modal.confirm({
        title: "고객사 전체 일괄 적용",
        content: defaultConfirmText(channel, route, overrides.value),
        okText: "바꾸기",
        cancelText: "취소",
        onOk: () => write(() => messageRouteApi.setDefault(channel, route)),
      });
    };

    // 회사 상태: 기본값 따름 = 회사 행 지우기, v1/v2 = 저장값으로 쓰기
    const changeCompany = (channel, state) => {
      const company = selectedCompany.value;
      if (!company) return;
      // companyName 은 표시용이고 crm 이 100자까지만 받는다(REQ-303)
      const name = (company.name || "").slice(0, 100) || undefined;
      write(() =>
        state === "default"
          ? messageRouteApi.clearCompany(company._id, channel)
          : messageRouteApi.setCompany(company._id, channel, ROUTE_OF_STATE[state], name)
      );
    };

    const clearCompany = (companyId, channel) => write(() => messageRouteApi.clearCompany(companyId, channel));

    // 채널별 개별 설정 모두 지우기 — 확인창을 거친다. 전체 기본값 행은 남는다.
    const confirmClearAll = (channel) => {
      Modal.confirm({
        title: `${channelLabel(channel)} 개별 설정 모두 지우기`,
        content: clearAllConfirmText(channel, overrides.value, defaults.value),
        okText: "모두 지우기",
        okType: "danger",
        cancelText: "취소",
        onOk: () => write(() => messageRouteApi.clearAllCompanies(channel)),
      });
    };

    onMounted(reloadAll);

    return {
      CHANNELS,
      DEFAULT_ROUTE_OPTIONS,
      COMPANY_STATE_OPTIONS,
      optionsFor,
      channelLabel,
      routeLabel,
      companyLabel,
      companyDetail,
      actorLabel,
      accounts,
      companies,
      defaultRouteOf,
      overrideCount,
      formatTime,
      overrideColumns,
      historyColumns,
      loaded,
      listLoading,
      saving,
      defaults,
      overrides,
      dispatcherConfigured,
      defaultRow,
      companyKeyword,
      companyResults,
      companyLoading,
      selectedCompany,
      selectedStates,
      searchCompanies,
      selectCompany,
      historyChannel,
      historyChannelOptions,
      historyOnlySelected,
      historyLoading,
      historyPagination,
      historyRows,
      loadHistory,
      reloadAll,
      confirmSetDefault,
      changeCompany,
      clearCompany,
      confirmClearAll,
    };
  },
};
</script>

<style scoped>
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}

.pageDesc {
  font-size: 14px;
  color: #6c757d;
  margin-top: 8px;
  display: block;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.content-wrap-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  padding: 24px;
}

.notice {
  margin-bottom: 16px;
}

.notice-list {
  margin: 0;
  padding-left: 18px;
}

.section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 8px;
}

.section-hint {
  font-size: 13px;
  color: #6c757d;
  margin: 8px 0 12px;
}

.route-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.route-row:last-of-type {
  border-bottom: none;
}

.route-channel {
  width: 110px;
  font-weight: 600;
}

.route-meta {
  font-size: 12px;
  color: #8c8c8c;
}

.company-search {
  width: 360px;
  max-width: 100%;
}

/* 병원 검색 결과 목록 (AfterdocMigration.vue 와 같은 모양) */
.company-results {
  margin-top: 8px;
  max-width: 560px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

.company-result-item {
  padding: 8px 12px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}

.company-result-item:last-child {
  border-bottom: none;
}

.company-result-item:hover {
  background: #f5f5f5;
}

.company-result-item.selected {
  background: #fffbe6;
  font-weight: 600;
  color: #ad6800;
}

.selected-company {
  margin: 10px 0 4px;
  padding: 8px 14px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  max-width: 560px;
}

/* 같은 이름 병원을 가려 보는 정보(관리자 아이디·주소·등록일·ID) */
.company-detail {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
  word-break: break-all;
}

.history-filter {
  margin-bottom: 12px;
}
</style>
